'use client';

import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

export type CardVariantProps = VariantProps<typeof cardVariants>;
type CardProps = CardVariantProps & {
  id?: string;
  children?: React.ReactNode;
  className?: string;
  title?: string;
  bodyClass?: string;
  style?: React.CSSProperties;
};
type CardImageProps = Omit<CardProps, 'bodyClass' | 'title'> & {
  src: string;
  alt: string;
  imgClassName?: string;
};
export type CardBaseProps = CardProps & React.HTMLAttributes<HTMLDivElement>;

const cardVariants = cva('flex flex-col', {
  variants: {
    gap: {
      none: '',
      exs: 'gap-y-1',
      xs: 'gap-y-2',
      sm: 'gap-y-4',
      md: 'gap-y-6',
      lg: 'gap-y-8',
    },
    padding: {
      none: '',
      xs: 'p-2',
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8',
    },
    variant: {
      default: 'bg-surface-card  border-edge-card',
      inner: 'bg-surface-raised border-edge-card rounded',
      parent: 'bg-transparent ',
    },
  },
  defaultVariants: {
    gap: 'md',
    padding: 'md',
    variant: 'default',
  },
});

export const CardBase = React.forwardRef<HTMLDivElement, CardBaseProps>(
  (
    { id, children, className, variant, padding = 'none', gap = 'none', style },
    ref
  ) => {
    return (
      <div
        id={id}
        ref={ref}
        className={twMerge(
          'card',
          'card border rounded-lg text-default ',
          cardVariants({ variant, padding, gap }),
          className
        )}
        style={style}
      >
        {children}
      </div>
    );
  }
);
CardBase.displayName = 'CardBase';

export const CardHeader = ({
  title,
  children,
  className,
  padding,
  gap,
}: CardProps) => {
  return (
    <div
      className={twMerge(
        'card-header rounded-t-lg min-h-[72px]',
        cardVariants({ padding, gap, variant: 'parent' }),
        className
      )}
    >
      {title && <h2 className="text-xl font-bold">{title}</h2>}
      {children}
    </div>
  );
};
export const CardImage = ({
  src,
  alt,
  imgClassName,
  children,
  className,
  padding,
}: CardImageProps) => {
  return (
    <div
      className={twMerge(
        'card-image rounded-t-lg overflow-hidden',
        cardVariants({ padding, variant: 'parent' }),
        className
      )}
    >
      {src && alt ? (
        <img
          src={src}
          alt={alt}
          className={twJoin('object-cover w-full h-full', imgClassName)}
        />
      ) : (
        children
      )}
    </div>
  );
};

export const CardBody = React.forwardRef<HTMLDivElement, CardProps>(
  ({ title, children, className, gap, padding }, ref) => {
    return (
      <div
        ref={ref}
        className={twMerge(
          'card-body',
          cardVariants({ padding, gap, variant: 'parent' }),
          className,
          '[.card-header+&]:pt-0'
        )}
      >
        {title && <h2 className="text-h2">{title}</h2>}
        {children}
      </div>
    );
  }
);
CardBody.displayName = 'CardBody';
export const CardFooter = ({
  children,
  className,
  gap,
  padding,
}: CardProps) => {
  return (
    <div
      className={twMerge(
        'card-footer rounded-b',
        cardVariants({ padding, gap, variant: 'parent' }),
        className
      )}
    >
      {children}
    </div>
  );
};

export const Card = ({
  id,
  title,
  children,
  className,
  bodyClass,
  style,
  ...rest
}: CardProps) => {
  return (
    <CardBase
      id={id}
      className={className}
      style={style}
      variant={rest?.variant}
    >
      {title && (
        <CardHeader {...rest}>
          <h2 className="text-h2">{title}</h2>
        </CardHeader>
      )}
      <CardBody className={twMerge(title && 'pt-0', bodyClass)} {...rest}>
        {children}
      </CardBody>
    </CardBase>
  );
};
